import { createId } from '../core/ids';
import { registerPattern, type PatternImage } from '../core/patterns';
import type { BlendMode, Rect } from '../core/types';
import { getEngine } from '../engine/engine';
import { fillSelectionWithPattern, offsetLayer } from '../engine/ops';
import { activeDocument, commitHistory } from './actions';
import { useAppStore } from './store';

const engine = getEngine();

function state() {
  return useAppStore.getState();
}

function clampRect(rect: Rect, size: { width: number; height: number }): Rect {
  const x = Math.max(0, Math.floor(rect.x));
  const y = Math.max(0, Math.floor(rect.y));
  return {
    x,
    y,
    width: Math.min(size.width - x, Math.ceil(rect.width)),
    height: Math.min(size.height - y, Math.ceil(rect.height)),
  };
}

/**
 * Edit - Define New - Pattern. The tile is the selection, or the whole layer when
 * nothing is selected; a rectangular selection is what makes a tile line up.
 */
export function definePatternFromLayer(): void {
  const doc = activeDocument();
  if (!doc?.activeLayerId) return;
  const resources = engine.peek(doc.id);
  const surface = resources?.layer(doc.activeLayerId)?.surface;
  if (!surface) {
    state().setStatus('Select a pixel layer to define a pattern from');
    return;
  }

  const bounds = resources.selection.isEmpty ? null : resources.selection.bounds();
  const rect = clampRect(bounds ?? { x: 0, y: 0, width: doc.width, height: doc.height }, doc);
  if (rect.width < 1 || rect.height < 1) return;

  const pattern: PatternImage = {
    id: createId('pattern'),
    name: `${doc.name} ${rect.width}×${rect.height}`,
    width: rect.width,
    height: rect.height,
    data: surface.readPixels(rect),
  };
  registerPattern(pattern);
  state().setActivePattern(pattern.id);
  state().setStatus(`Defined a ${rect.width}×${rect.height} pattern`);
}

export function fillWithPattern(
  pattern: PatternImage,
  options: { opacity: number; blendMode: BlendMode },
): void {
  const doc = activeDocument();
  if (!doc?.activeLayerId) return;
  commitHistory(
    doc.id,
    'Pattern Fill',
    fillSelectionWithPattern(engine, doc, doc.activeLayerId, pattern, options),
  );
}

/** Filter - Other - Offset, the seam-hunting step of making a tileable texture. */
export function applyOffsetFilter(dx: number, dy: number, wrap: boolean): void {
  const doc = activeDocument();
  if (!doc?.activeLayerId) return;
  commitHistory(doc.id, 'Offset', offsetLayer(engine, doc, doc.activeLayerId, dx, dy, wrap));
}
